import { useEffect, useMemo, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

import {
  MapWorkbench,
  MapWorkspaceView,
  defaultThemes,
  type MapWorkbenchBehavior,
  type MapWorkbenchDataAdapter,
  type MapWorkbenchMode,
  type MapWorkbenchRuntimeFeatureStateHydrationArgs,
  type MapWorkbenchRuntimeFeatureStateHydrationResult,
  type MapWorkbenchRuntimeSurfacePanelProps,
  type MapWorkbenchRuntimeSurfaceSectionRenderArgs,
  type MapWorkbenchStyleRecord,
  type MapWorkbenchFeatureInspection,
} from '@workspace/ui';
import { LayerExperiencePane, type LayerExperience } from '@workspace/silk-ui';

import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import { useNavigation } from '@/context/NavigationContext';
import { usePreferences } from '@/context/PreferencesContext';
import {
  buildCompositionMetadata,
  buildGlyphRuntimeUrl,
  buildGlyphSchemeUrl,
  buildOverlayStyleFromRuntime,
  buildSpriteSchemeUrl,
  buildSpriteRuntimeBaseUrl,
  rewritePreviewStyleToUseSourceArchives,
  isCustomMapAsset,
  isPortalAssetReadOnly,
  normalizeShareScope,
  parseGlyphSetIdFromStyleUrl,
  parseSpriteDependencyRefsFromStyleRoot,
  parseSpriteSetIdFromStyleUrl,
} from '@/lib/mapping-assets';
import {
  createMapStyle,
  executeMapExperienceResolver,
  getMapStyle,
  queryMapSourceGeoJSON,
  getSpriteSetImage,
  getSpriteSetManifest,
  listGlyphSets,
  listMapSources,
  listSpriteSets,
  previewMapStyle,
  updateMapStyle,
  type GlyphSet,
  type MappingQueryContext,
  type MapSource,
  type MapStyle,
  type MapStyleCreateRequest,
  type MapStylePreviewResponse,
  type MapViewState,
  type SpriteSet,
} from '@/lib/mapping-api';
import { composeStyleWithSystemBasemap } from '@/lib/systemBasemap';
import { useMappingAccess } from '@/lib/useMappingAccess';

const EMPTY_LAYER_EXPERIENCE: LayerExperience = {
  id: '__no-layer__',
  source_id: '',
  source_layer: '',
  label: 'Layer Editor',
  description: 'Add a layer to start styling this map.',
  visibility: { default_visible: false },
  appearance: { variants: [] },
  filters: { definitions: [] },
};

export default function MapStyleWorkbenchPage({
  styleId,
  assetMode,
  modeOverride,
  mobileRightPaneDefaultOpen,
  viewerHideIdleRightPane,
  hideViewModeToolbar,
  runtimeOverlayGeoJSON,
  runtimeOverlayStyle,
  runtimeOverlayInteractive,
  isRuntimeOverlayFeatureInteractive,
  onRuntimeOverlayFeatureHover,
  onRuntimeOverlayFeatureClick,
  onPinnedFeatureChange,
  renderRuntimeSurfaceSection,
  runtimeSurfacePanelProps,
  hydrateRuntimeFeatureState,
  mappingQueryContext,
}: {
  styleId?: string;
  assetMode?: 'style' | 'custom-map';
  modeOverride?: MapWorkbenchMode;
  mobileRightPaneDefaultOpen?: boolean;
  viewerHideIdleRightPane?: boolean;
  hideViewModeToolbar?: boolean;
  runtimeOverlayGeoJSON?: Record<string, unknown> | null;
  runtimeOverlayStyle?: {
    fillPaint?: Record<string, unknown> | null;
    linePaint?: Record<string, unknown> | null;
    pointPaint?: Record<string, unknown> | null;
  } | null;
  runtimeOverlayInteractive?: boolean;
  isRuntimeOverlayFeatureInteractive?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  }) => boolean;
  onRuntimeOverlayFeatureHover?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  } | null) => void;
  onRuntimeOverlayFeatureClick?: (feature: {
    featureId: string | number | null;
    geometryType: string | null;
    properties: Record<string, unknown>;
  }) => void;
  onPinnedFeatureChange?: (
    feature: MapWorkbenchFeatureInspection | null,
    trigger: 'click' | 'context_menu',
  ) => void;
  renderRuntimeSurfaceSection?: (args: MapWorkbenchRuntimeSurfaceSectionRenderArgs) => ReactNode;
  runtimeSurfacePanelProps?: MapWorkbenchRuntimeSurfacePanelProps;
  hydrateRuntimeFeatureState?: (
    args: MapWorkbenchRuntimeFeatureStateHydrationArgs,
  ) => Promise<MapWorkbenchRuntimeFeatureStateHydrationResult | null>;
  mappingQueryContext?: MappingQueryContext | null;
}) {
  const { navigate } = useNavigation();
  const { basemapTheme } = usePreferences();
  const mappingAccess = useMappingAccess();
  const fallbackPath = assetMode === 'custom-map' ? '/maps/custom-maps' : '/maps/styles';
  const defaultMode =
    modeOverride ??
    resolvePortalWorkbenchMode({
      assetMode,
      styleId,
      search: typeof window !== 'undefined' ? window.location.search : '',
    });

  const basemapThemeLabel = defaultThemes.find(theme => theme.value === basemapTheme)?.label ?? basemapTheme;

  const adapter = useMemo<MapWorkbenchDataAdapter>(
    () => ({
      loadStyle: id => getMapStyle(id, mappingQueryContext) as any,
      listSources: params => listMapSources({ limit: 200, share_scope: params.share_scope }, mappingQueryContext) as any,
      listSpriteSets: params => listSpriteSets({ limit: 200, share_scope: params.share_scope }),
      getSpriteSetManifest: id => getSpriteSetManifest(id) as any,
      getSpriteSetImage: id => getSpriteSetImage(id),
      getSpriteSetPreviewManifest: id => getSpriteSetManifest(id, { variant: 'preview' }) as any,
      getSpriteSetPreviewImage: id => getSpriteSetImage(id, { variant: 'preview' }),
      listGlyphSets: params => listGlyphSets({ limit: 200, share_scope: params.share_scope }),
      previewStyle: async style_json => {
        const payload = await previewMapStyle({ style_json });
        return {
          ...payload,
          style_json:
            rewritePreviewStyleToUseSourceArchives(payload.style_json ?? null, mappingQueryContext) ?? payload.style_json,
        } as MapStylePreviewResponse;
      },
      queryGeoJSONSource: (id, payload) => queryMapSourceGeoJSON(id, payload as any, mappingQueryContext),
      executeInteractivityResolver: payload => executeMapExperienceResolver(payload),
      createStyle: payload => createMapStyle(payload as MapStyleCreateRequest) as Promise<MapStyle>,
      updateStyle: (id, payload) => updateMapStyle(id, payload as any) as Promise<MapStyle>,
    }),
    [mappingQueryContext]
  );

  const behavior = useMemo<MapWorkbenchBehavior>(
    () => ({
      isCustomMapAsset: isCustomMapAsset as any,
      buildOverlayStyleFromRuntime: (styleJson, metadata) =>
        buildOverlayStyleFromRuntime(styleJson, metadata as any),
      rewritePreviewStyleToRuntimeSources: styleJson =>
        rewritePreviewStyleToUseSourceArchives(styleJson as any, mappingQueryContext),
      buildMetadata: ({
        existingMetadata,
        styleJson,
        authoredStyleJson,
        sources,
        spriteSet,
        spriteEntries,
        activeSpriteId,
        glyphSet,
        mapExperience,
        initialView,
        isCustomMap,
      }) =>
        buildCompositionMetadata({
          existingMetadata,
          styleJson,
          authoredStyleJson: authoredStyleJson ?? null,
          sources: sources as MapSource[],
          sprite: spriteSet as SpriteSet | null,
          sprites: (spriteEntries ?? []).map(entry => ({
            id: entry.id,
            sprite_set_id: entry.spriteSet.id ?? '',
            name: entry.spriteSet.name ?? entry.spriteSet.id ?? entry.id,
            share_scope: entry.spriteSet.share_scope ?? 'tenant',
          })),
          activeSpriteId,
          glyph: glyphSet as GlyphSet | null,
          mapExperience: (mapExperience ?? null) as Record<string, unknown> | null,
          customMap: isCustomMap,
          initialView: initialView as MapViewState | null,
        }),
      buildSpriteUrl: buildSpriteSchemeUrl,
      buildGlyphUrl: buildGlyphSchemeUrl,
      buildSpriteRuntimeUrl: buildSpriteRuntimeBaseUrl,
      buildGlyphRuntimeUrl: buildGlyphRuntimeUrl,
      inferSpriteDependencies: styleJson =>
        parseSpriteDependencyRefsFromStyleRoot(styleJson?.sprite).map((entry, index) => ({
          id: entry.id,
          spriteSetId: entry.sprite_set_id,
          sortOrder: index,
        })),
      inferSpriteSetId: styleJson => parseSpriteSetIdFromStyleUrl(styleJson?.sprite),
      inferGlyphSetId: styleJson => parseGlyphSetIdFromStyleUrl(styleJson?.glyphs),
      composeStyleWithBasemap: (overlayStyle, theme) => composeStyleWithSystemBasemap(overlayStyle, theme as typeof basemapTheme),
      normalizeShareScope: (value, fallback) => normalizeShareScope(value ?? fallback),
    }),
    [basemapTheme, mappingQueryContext]
  );

  if (!mappingAccess.mappingFeatureEnabled) {
    return <MappingFeatureUnavailable />;
  }

  if (!mappingAccess.mappingPermissionGranted) {
    return <MappingPermissionDenied />;
  }

  useEffect(() => {
    if (!styleId) {
      navigate(fallbackPath);
    }
  }, [fallbackPath, navigate, styleId]);

  if (!styleId) {
    return null;
  }

  const RuntimeComponent = defaultMode === 'view' ? MapWorkspaceView : MapWorkbench;

  return (
    <RuntimeComponent
      styleId={styleId}
      assetMode={assetMode}
      adapter={adapter}
      behavior={behavior}
      navigate={navigate}
      basemapTheme={basemapTheme}
      basemapThemeLabel={basemapThemeLabel}
      canManageGlobal={false}
      defaultShareScope="tenant"
      shareScopeOptions={[
        { value: 'public', label: 'Public' },
        { value: 'shared', label: 'Shared' },
        { value: 'tenant', label: 'Tenant' },
      ]}
      {...(defaultMode === 'view' ? { mode: defaultMode } : { defaultMode })}
      defaultViewState={{
        center: [-84.5, 44.5],
        zoom: 7,
        bearing: 0,
        pitch: 0,
      }}
      mobileRightPaneDefaultOpen={mobileRightPaneDefaultOpen}
      viewerHideIdleRightPane={viewerHideIdleRightPane}
      hideViewModeToolbar={hideViewModeToolbar}
      runtimeOverlayGeoJSON={runtimeOverlayGeoJSON}
      runtimeOverlayStyle={runtimeOverlayStyle}
      runtimeOverlayInteractive={runtimeOverlayInteractive}
      isRuntimeOverlayFeatureInteractive={isRuntimeOverlayFeatureInteractive}
      onRuntimeOverlayFeatureHover={onRuntimeOverlayFeatureHover}
      onRuntimeOverlayFeatureClick={onRuntimeOverlayFeatureClick}
      onPinnedFeatureChange={onPinnedFeatureChange}
      rootClassName="h-full min-h-0 w-full"
      renderRuntimeSurfaceSection={renderRuntimeSurfaceSection}
      runtimeSurfacePanelProps={runtimeSurfacePanelProps}
      hydrateRuntimeFeatureState={hydrateRuntimeFeatureState}
      resolveOwnership={(style, isCustomMap) =>
        resolvePortalWorkbenchOwnership({
          style,
          isCustomMap,
          requestedMode: defaultMode,
          featureTenantId: mappingAccess.featureTenantId,
        })
      }
      renderDesktopAuthoringPane={({
        activeSection,
        availableFields,
        stylePane,
        spriteRootSelector,
        layerExperience,
        onSectionChange,
        onUpdateLayer,
        renderSection,
        sections,
        target,
        mode,
      }) => {
        const canOpenSources = sections.includes('sources');

        if (!target || !layerExperience) {
          const renderNoLayerSection = (section: (typeof sections)[number]) => {
            if (section === 'sources') {
              return renderSection('sources');
            }
            const sectionLabel =
              section === 'style'
                ? 'style'
                : section === 'filters'
                  ? 'filters'
                  : section === 'interactions'
                    ? 'interactions'
                    : section === 'data'
                      ? 'data resolvers'
                      : section === 'source-info'
                        ? 'source information'
                        : 'advanced settings';

            return (
              <div className="flex h-full min-h-[18rem] items-center justify-center">
                <div className="w-full max-w-sm rounded-md border border-dashed border-border/60 bg-muted/10 px-5 py-6 text-center">
                  <div className="text-sm font-medium text-foreground">No layer on the map</div>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    Open Sources to add a source or enable a source layer before editing {sectionLabel}.
                  </p>
                  {canOpenSources ? (
                    <button
                      type="button"
                      onClick={() => onSectionChange('sources')}
                      className="mt-4 inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                    >
                      Open Sources
                    </button>
                  ) : null}
                </div>
              </div>
            );
          };

          return (
            <LayerExperiencePane
              layer={EMPTY_LAYER_EXPERIENCE}
              activeSection={activeSection}
              onSectionChange={onSectionChange}
              availableFields={availableFields}
              stylePane={stylePane}
              spriteRootSelector={spriteRootSelector}
              filterMode={mode}
              className="h-full w-full border-l-0 shadow-none"
              sections={sections}
              renderSection={renderNoLayerSection}
            />
          );
        }

        return (
          <LayerExperiencePane
            layer={layerExperience}
            activeSection={activeSection}
            onSectionChange={onSectionChange}
            onUpdateLayer={onUpdateLayer ?? undefined}
            availableFields={availableFields}
            stylePane={stylePane}
            spriteRootSelector={spriteRootSelector}
            filterMode={mode}
            className="h-full w-full border-l-0 shadow-none"
            sections={sections}
            renderSection={renderSection}
          />
        );
      }}
    />
  );
}

const isWorkbenchMode = (value: string | null): value is MapWorkbenchMode =>
  value === 'author' || value === 'view' || value === 'customize-save-copy';

export function resolvePortalWorkbenchMode({
  assetMode,
  styleId,
  search,
}: {
  assetMode?: 'style' | 'custom-map';
  styleId?: string;
  search?: string;
}): MapWorkbenchMode {
  const params = new URLSearchParams(search ?? '');
  const explicitMode = params.get('mode');
  if (isWorkbenchMode(explicitMode)) {
    return explicitMode;
  }
  if (assetMode === 'custom-map' && styleId) {
    return 'view';
  }
  return 'author';
}

export function resolvePortalWorkbenchOwnership({
  style,
  isCustomMap,
  requestedMode,
  featureTenantId,
}: {
  style?: Pick<MapWorkbenchStyleRecord, 'tenant_id'> | null;
  isCustomMap: boolean;
  requestedMode: MapWorkbenchMode;
  featureTenantId?: string;
}) {
  const hasOwner = typeof style?.tenant_id === 'string' && style.tenant_id.length > 0;
  const readOnly = hasOwner ? isPortalAssetReadOnly(style?.tenant_id, featureTenantId) : false;

  if (!readOnly) {
    return {
      readOnly: false,
      mode: requestedMode,
    };
  }

  const assetLabel = isCustomMap ? 'custom map' : 'style';

  if (requestedMode === 'view') {
    return {
      readOnly: true,
      mode: 'view' as const,
      notice: `This ${assetLabel} is shared into the current tenant context. You are viewing the published workspace. Save a copy to make tenant-owned changes.`,
    };
  }

  if (!isCustomMap && requestedMode === 'author') {
    return {
      readOnly: true,
      mode: 'author' as const,
      notice: `This ${assetLabel} is shared into the current tenant context. You can continue authoring here, but saving will create a new tenant-owned ${assetLabel}.`,
    };
  }

  return {
    readOnly: true,
    mode: 'customize-save-copy' as const,
    notice: `This ${assetLabel} is shared into the current tenant context. Changes here will be saved as a new tenant-owned ${assetLabel}.`,
  };
}
